// react
import React, { useState } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Box, Typography, Snackbar, Alert, CircularProgress } from "@mui/material";

// artifacts/cdn
import ArchivedDocsTable from "./ArchivedDocsTable";
import DetailsDialog from "./DetailsDialog";
import PreservationProofDialog from "./PreservationProofDialog";

// components
import FileViewerDialog from "components/dialogs/FileViewerDialog";

// firebase
import { db } from "config/firebase";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

// services/storage
import downloadFile from "services/storage/downloadFile";

// A ---------------------------------------------------------------------- M

const CDNFileViewer = ({ tag, isTagGroupMember }) => {
  const { t } = useTranslation();
  const functions = getFunctions();

  // Stati
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [record, setRecord] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [xmlContent, setXmlContent] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [numPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileLoadingLabel, setFileLoadingLabel] = useState("");
  const [preservationLoading, setPreservationLoading] = useState(false);
  const [preservationData, setPreservationData] = useState({ uploadReceipt: null, aicData: null });

  // Drawers + snackbars
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [isPreservationProofDialogOpen, setIsPreservationProofDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const handleOpenDetails = (record) => {
    setDetailsLoading(true);
    setSelectedRecord(record);
    setDetailsOpen(true);
    setDetailsLoading(false);
  };

  const handleCloseDetails = () => {
    setSelectedRecord(null);
    setDetailsOpen(false);
  };

  const handleOpenPreservationProofDialog = async (record) => {
    setPreservationLoading(true);
    setRecord(record);
    setIsPreservationProofDialogOpen(true);

    try {
      const data = {};

      if (record.txid) {
        const snap = await getDoc(doc(db, "uploadReceipts", record.txid));
        if (snap.exists()) data.uploadReceipt = snap.data();
      }

      if (record.AIC_ID) {
        const snap = await getDoc(doc(db, "aicsdata", record.AIC_ID));
        if (snap.exists()) data.aicData = snap.data();
      }

      setPreservationData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setPreservationLoading(false);
    }
  };

  const handleClosePreservationProofDialog = () => {
    setIsPreservationProofDialogOpen(false);
    setPreservationData({ uploadReceipt: null, aicData: null });
  };

  const handleCloseDialog = () => {
    setSelectedFile(null);
    setXmlContent("");
    setTextContent("");
    setCurrentPage(1);
    setFileLoading(false);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const openAndParse = async (url) => {
    try {
      const resp = await fetch(url);

      if (!resp.ok) {
        throw new Error(`Failed to fetch. Status ${resp.status}`);
      }

      const blob = await resp.blob();
      const type = blob.type;
      setSelectedFileType(type);

      if (type === "application/pdf") {
        setSelectedFile(url);
      } else if (type === "text/xml" || type === "application/xml") {
        setXmlContent(await blob.text());
        setSelectedFile(url);
      } else if (type === "text/plain" || type === "text/csv") {
        setTextContent(await blob.text());
        setSelectedFile(url);
      } else if (type === "application/json") {
        const txt = await blob.text();
        try {
          setTextContent(JSON.parse(txt));
        } catch {
          throw new Error("Invalid JSON");
        }
        setSelectedFile(url);
      } else if (type.startsWith("image/") || type.startsWith("video/")) {
        setSelectedFile(url);
      } else {
        throw new Error("Unsupported type:" + type);
      }

      setSnackbarMessage(t("success_opening_file"));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error opening file:", err);
      setSnackbarMessage(t("error_opening_file"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handlePreview = async (storagePath, name) => {
    try {
      const getUrl = httpsCallable(functions, "getSignedDownloadUrl");
      const { data } = await getUrl({ storagePath, tagId: tag.id });
      const signedUrl = data.url;
      await openAndParse(signedUrl, name);
    } catch (err) {
      console.error("Error in handlePreview:", err);
      setSnackbarMessage(t("error_opening_file"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleOpenFile = async (fileInfo) => {
    try {
      setFileLoadingLabel(t("loading_file"));
      setFileLoading(true);

      if (fileInfo.storagePath && fileInfo.fileName) {
        await handlePreview(fileInfo.storagePath, fileInfo.fileName);
      } else if (fileInfo.attachment && fileInfo.fileName) {
        await openAndParse(fileInfo.attachment, fileInfo.fileName);
      } else {
        console.warn("No valid field to open file:", fileInfo);
      }
    } finally {
      setFileLoading(false);
      setFileLoadingLabel("");
    }
  };

  const handleDownloadFile = async (fileInfo) => {
    const name = fileInfo.fileName;

    try {
      setFileLoadingLabel(t("downloading_file"));
      setFileLoading(true);

      let url;

      if (fileInfo.storagePath) {
        const getUrl = httpsCallable(functions, "getSignedDownloadUrl");
        const { data } = await getUrl({ storagePath: fileInfo.storagePath, tagId: tag.id });
        url = data.url;
      } else {
        url = fileInfo.attachment;
      }

      await downloadFile(url, name);

      setSnackbarMessage(t("success_downloading_file"));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error downloading file:", err);
      setSnackbarMessage(t("error_downloading_file"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setFileLoading(false);
      setFileLoadingLabel("");
    }
  };

  const renderPreview = () =>
    selectedFileType && (
      <FileViewerDialog
        open={!!selectedFile}
        onClose={handleCloseDialog}
        fileType={selectedFileType}
        fileUrl={selectedFile}
        xmlContent={xmlContent}
        textContent={textContent}
        currentPage={currentPage}
        numPages={numPages}
        onPageChange={handlePageChange}
      />
    );

  return (
    <>
      {tag && <ArchivedDocsTable tag={tag} isTagGroupMember={isTagGroupMember} onOpenDetails={handleOpenDetails} onOpenPreservationProof={handleOpenPreservationProofDialog} />}

      {selectedRecord && (
        <DetailsDialog open={detailsOpen} record={selectedRecord} loading={detailsLoading} onClose={handleCloseDetails} handleOpenFile={handleOpenFile} handleDownloadFile={handleDownloadFile} />
      )}

      {fileLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            backgroundColor: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              px: 4,
              py: 3,
              borderRadius: 3,
              backgroundColor: "background.paper",
              boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography fontWeight={600}>{fileLoadingLabel}</Typography>
          </Box>
        </Box>
      )}

      {isPreservationProofDialogOpen && record && (
        <PreservationProofDialog
          record={record}
          open={isPreservationProofDialogOpen}
          loading={preservationLoading}
          uploadReceipt={preservationData.uploadReceipt}
          aicData={preservationData.aicData}
          onClose={handleClosePreservationProofDialog}
          handleDownloadFile={handleDownloadFile}
        />
      )}

      {renderPreview()}

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CDNFileViewer;
